/**
 * FigureHistoryList — 그림 하나의 지난 버전을 늘어놓고 고르게 한다.
 *
 * 계약 §3-4·§4-4. versions 는 최신순으로 온다. 이력은 append-only 라 되돌려도 과거 버전은 남고
 * 새 버전이 쌓인다 — 그래서 「되돌리면 사라진다」는 경고를 하지 않는다.
 *
 * ⚠ 이 목록을 부르는 것 자체가 **첫 조회면 version 1(관측)을 기록한다**(계약 §6).
 * 그래서 창을 열 때 자동으로 부르지 않고, 사용자가 [이미지 교체] 칸을 펼 때 부른다.
 *
 * 2026-08-29 회신(§2 Q8 · §4-1)으로 두 가지가 늘었다 —
 *   · 사유 **700(삭제)**. 이 버전에는 [이 버전 가져오기]를 내지 않는다(아래 주석).
 *   · `versions[].user_prompt` — 재요청할 때 사람이 쓴 지시. 어느 버전이 무슨 지시로 나왔는지
 *     화면에 없으면, 개수를 틀리는 그림(계약 §6: 원 8개를 7개로)에서 지시를 바꿔 가며 여러 번
 *     재요청할 때 v3·v5·v7 이 사유·시각 말고는 구분되지 않는다.
 */
import { RotateCcw, Eye } from 'lucide-react';
import { Button } from '../common';
import V2DateTime from '../common/V2DateTime';
import { figureSourceName, figureVersionRow, FIGURE_LOST_CONTENT_TEXT } from './figureText';

export default function FigureHistoryList({
  versions, currentVersion, loading, error, onLoad, onRetry,
  /**
   * 창이 지금 서버에 무언가 보내는 중인가 — 그동안 이 목록의 버튼을 잠근다.
   *
   * 종전 이름은 `busyVersion`(진행 중인 버전 번호)이었는데, 그 값이 숫자였던 것은 restore 를
   * 쓰던 때뿐이고 2026-08-30 에 그 경로를 걷어내면서 **늘 null 이 됐다** — 이름만 남고 아무것도
   * 안 잠그는 죽은 값이었다. 이제 창의 실제 상태를 참거짓으로 받는다.
   */
  busy = false,
  /**
   * **가져올 수 있는 상태인가**(2026-08-31 버그 수정).
   *
   * [이 버전 가져오기]는 창의 `loadVersion` 을 부르는데 그쪽이 `writable` 이 아니면 곧바로
   * 되돌아 나간다. 그 사실이 여기 없어서, 창이 읽기전용일 때 **이 버튼은 눌리는데 아무 일도
   * 일어나지 않았다** — 오너 보고 2026-08-31 「이 부분이 새로고침이 안 된다」가 이것이다.
   * 같은 동작의 쌍둥이 버튼(창 위 미리보기 상자)은 그때 잠겨 있어, 한 화면이 두 말을 하고 있었다.
   *
   * [보기]에는 걸지 않는다 — 그것은 서버에 보내지도, 값을 바꾸지도 않는다. 읽기전용이어도 봐야 한다.
   */
  canLoad = true,
  /**
   * **사진 버전을 고르는 길**(2026-09-01). 이력 응답에는 그 버전의 그림이 없다(계약 §3-4 —
   * `versions[]` 는 spec·사유·시각·설명뿐). 그런데 사진 버전이 **돌아갈 곳은 서버가 보관한
   * 원본 한 장**이라(§4-2 — 보관은 픽셀→SVG 첫 전환 때 한 번뿐), 그 한 장을 그리면
   * 「이 버전을 고르면 무엇이 되는가」는 정확히 답할 수 있다. 그래서 사진 줄의 그림·미리보기가
   * 모두 `originalSrc` 한 장이고, 그 까닭을 목록 머리글이 말한다.
   *
   * 고르기는 **서버에 나가지 않는다** — 저장은 창의 [저장]이 한다(FigureModal.pickOriginal).
   */
  onPickOriginal = null, canPickOriginal = false, originalSrc = null, originalHint = '',
  /**
   * **내용이 남은 판(has_content=true)을 고르는 길**(계약 「판 내용 스냅샷」 09-01).
   * 사진 줄과 돌아가는 곳이 다르다 — 보관 원본 한 장이 아니라 **그 판의 실제 그림**(글자 수정
   * 포함)이고, [저장]이 restore 를 1회 보낸다(FigureModal.doRestore — PATCH 로는 실을 수 없다).
   * 고르기는 여전히 서버에 나가지 않는다.
   */
  onPickContent = null,
  /**
   * [보기]가 받아 둔 판 그림 캐시 { [version]: {src,…} | 'loading' | null }. 있으면 그 줄의 작은
   * 그림으로 쓴다. **여기서 채우지 않는다** — src 가 커서 목록을 열자마자 전 판을 받는 길은
   * 계약이 막았고(「lazy 권장」), 받는 시점은 창의 [보기] 하나다(FigureModal 의 verSrc effect).
   */
  verSrc = {},
  // 미리보기 — 누르면 **그려만 본다**(서버에 아무것도 보내지 않는다).
  // 2026-08-30 오너 지적 「다른 버전의 그림으로 바꿀 때마다 저장되는데 문제가 있어 보인다」:
  // 이력은 append-only 라(계약 §1-5) 옛 [이 버전으로] 를 훑어보듯 누르면 그때마다 새 버전이 쌓이고,
  // 되돌릴 버전을 **눈으로 고를 길이 없었다.** 보는 것과 정하는 것을 갈랐다.
  previewVersion = null, onPreview = null,
  // 불러와서 편집 중인 버전. 서버의 현재 버전과 **다르다** — 불러오기는 저장이 아니다.
  loadedVersion = null,
  /**
   * 버전마다 미리 그려 둔 작은 그림 `{ [version]: { svg, width, height } | null }`.
   * 있으면 **누르지 않고도** 어느 버전인지 알 수 있다 — 그것이 이 목록의 목적이다.
   *
   * 2026-09-01 에 값이 문자열에서 객체가 됐다 — 크기를 함께 담아야 AI 요청 창이 그 SVG 를
   * PNG 로 구울 수 있다(figureImage 는 크기가 0 이면 거절한다). 여기서는 `svg` 만 쓴다.
   */
  thumbs = {},
}) {
  if (loading) {
    return (
      <div className="space-y-1.5 p-2">
        {[0, 1, 2].map((i) => <div key={i} className="h-9 rounded-lg bg-gray-100 motion-safe:animate-pulse" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-2 p-3">
        <p role="alert" className="text-xs text-danger">{error}</p>
        {onRetry && <Button size="xs" variant="secondary" onClick={onRetry}>다시 시도</Button>}
      </div>
    );
  }
  const list = Array.isArray(versions) ? versions : [];
  if (list.length === 0) {
    return <p className="p-3 text-xs text-gray-400">이력이 없습니다.</p>;
  }
  return (
    <ul className="divide-y divide-gray-100">
      {list.map((v) => {
        // 어느 버튼을 낼지는 **한자리에서 정해져 온다**(figureText.figureVersionRow) —
        // 갈래가 넷이라 여기서 조건을 다시 짜면 시험으로 덮이지 않는 자리가 생긴다.
        const row = figureVersionRow(v, currentVersion, { canPickOriginal, canShowOriginal: !!originalSrc });
        const { isCurrent } = row;
        const isPreviewing = previewVersion != null && Number(v.version) === Number(previewVersion);
        const isLoaded = loadedVersion != null && Number(v.version) === Number(loadedVersion);
        return (
          <li key={v.version} className={`flex flex-wrap items-center gap-2 px-3 py-2 ${isPreviewing ? 'bg-primary/5' : ''}`}>
            {/* 그림을 먼저 보인다 — 「어느 버전인가」는 사유·시각보다 그림이 빨리 말한다.
                아직 안 그려졌으면 자리만 잡아 목록이 들썩이지 않게 한다. */}
            <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-white">
              {row.hasSpec
                ? (thumbs[v.version]?.svg
                  ? <img src={thumbs[v.version].svg} alt={`v${v.version} 그림`} className="size-full object-contain" />
                  : <span className="size-full animate-pulse bg-gray-100" aria-hidden="true" />)
                : row.hasContent
                  /* 내용이 남은 판 — 여기서 미리 받지 않는다(lazy · src 가 크다). [보기]가 받아 둔
                     것이 있으면 그리고, 없으면 받는 길을 이름으로 부른다 — 로딩 무늬를 내면
                     「곧 온다」는 거짓 약속이 된다(아무도 요청하지 않았다). */
                  ? (verSrc[v.version]?.src
                    ? <img src={verSrc[v.version].src} alt={`v${v.version} 그림`} className="size-full object-contain" />
                    : <span className="px-0.5 text-center text-[9px] leading-tight text-gray-400">[보기]로 확인</span>)
                  /* 사진 줄 — 이 버전의 그림이 남아 있지 않으므로, **고르면 무엇이 되는지**를
                     그린다(보관 원본 한 장). 그것마저 못 받았으면 이름만 남긴다. */
                  : (originalSrc
                    ? <img src={originalSrc} alt="사진(보관 원본)" className="size-full object-contain" />
                    : <span className="px-0.5 text-center text-[9px] leading-tight text-gray-400">사진</span>)}
            </span>
            <span className="w-8 shrink-0 text-xs font-bold text-gray-700">v{v.version}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{figureSourceName(v)}</span>
            {/* 명세가 없는 버전 — 내용이 남은 판(hasContent)은 제 그림이 있으니 「사진」이라
                부르지 않는다. 도입 전 판(lostContent)은 **왜 같은 사진이 또 보이는지**를 말한다
                (계약 「판 내용 스냅샷」의 한계 — 말하지 않으면 고장으로 읽힌다). */}
            {!row.hasSpec && !row.hasContent && (
              <span className="text-[11px] text-gray-400">
                {row.lostContent ? FIGURE_LOST_CONTENT_TEXT : originalSrc ? '사진' : '사진 · 보여 줄 그림 없음'}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-[11px] text-gray-400" title={v.alt || ''}>{v.alt || ''}</span>
            <V2DateTime value={v.created_at} className="shrink-0 text-[11px] text-gray-400" />
            {v.created_by && <span className="shrink-0 text-[11px] text-gray-400">{v.created_by}</span>}
            {/* 보기 — 서버에 보내지 않는다. 「지금 이 버전」에도 낸다: 되돌린 뒤 원래 것과 견줘 볼 수 있어야 한다.
                명세가 없는 버전(원본 픽셀)은 그릴 것이 없어 잠근다. */}
            {onPreview && (
              <Button size="xs" variant={isPreviewing ? 'primary' : 'secondary'} icon={<Eye className="size-3" />}
                disabled={busy || !row.canPreview}
                title={!row.canPreview
                  ? '이 버전은 사진인데 서버가 보관한 원본을 받지 못해 그려 볼 것이 없습니다.'
                  : isPreviewing ? '미리보기를 끕니다'
                    : row.hasSpec
                      ? '저장하지 않고 이 버전을 그려 봅니다'
                      : row.hasContent
                        ? '이 판의 실제 그림을 서버에서 받아 그려 봅니다 — 저장되지 않습니다'
                        : '이 버전을 고르면 무엇이 되는지 그려 봅니다 — 서버가 보관한 사진 원본입니다'}
                onClick={() => onPreview(isPreviewing ? null : v.version)}>
                {isPreviewing ? '보는 중' : '보기'}
              </Button>
            )}
            {/* 서버에 저장된 버전 — 「지금」이라 부를 수 있는 것은 이것 하나다. */}
            {isCurrent && (
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">지금 이 그림</span>
            )}
            {/* 불러와 편집 중인 버전 — 아직 서버에 없다. 「지금」과 같은 얼굴로 그리면
                이미 저장된 것으로 읽히므로 색과 말을 달리한다. */}
            {isLoaded && !isCurrent && row.hasSpec && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">가져옴 · 저장 전</span>
            )}
            {row.action === 'none' ? null : row.action === 'blocked' ? (
              /* 고를 수 없는 줄이 둘이라 **이유도 둘이다** — 하나로 뭉개면 한쪽에 거짓말이 된다.
                 ① 삭제 버전(700): 이력이 「퇴장(조회 불가)」이라 되돌리면 무엇이 되는지 계약에 없다
                    (회신 §2 Q8). 누르면 무슨 일이 날지 모르는 버튼을 내지 않는다.
                 ② 사진인데 돌아갈 보관 원본이 없다: 골라도 `spec:null` 이 409 로 거절된다.
                 어느 쪽이든 왜 없는지를 자리에 남긴다 — 빈칸은 고장으로 읽힌다. */
              <span className="shrink-0 text-[11px] text-gray-400"
                title={row.isDeleted
                  ? '지운 버전입니다 — 그 그림의 이력은 퇴장했다고 계약이 적어(회신 §2 Q8) 가져올 값이 없습니다.'
                  : (originalHint || '이 버전은 사진인데 돌아갈 보관 원본이 서버에 없습니다.')}>
                {row.isDeleted ? '가져올 수 없음' : '바꿀 수 없음'}
              </span>
            ) : row.action === 'restoreContent' ? (
              /* **내용이 남은 판 — 고르기다. 서버에 나가지 않는다.** 창의 [저장]이 restore 를
                 1회 보낸다(FigureModal.doRestore) — 이 판의 그림은 PATCH 로 실을 수 없다.
                 canLoad 로 잠근다(창이 읽기전용이면 pickContentVersion 이 곧바로 되돌아 나간다 —
                 [이 버전 가져오기]와 같은 이유로, 눌리는데 아무 일도 없는 죽은 버튼을 내지 않는다). */
              <Button size="xs" variant={isLoaded ? 'primary' : 'secondary'} icon={<RotateCcw className="size-3" />}
                disabled={busy || !canLoad || !onPickContent || isLoaded}
                title={isLoaded
                  ? '이미 골랐습니다 — 창의 [저장]을 누르면 반영됩니다'
                  : !canLoad
                    ? '지금은 바꿀 수 없습니다 — 창 위 안내를 보세요'
                    : '이 판의 그림으로 바꾸기를 고릅니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                onClick={() => onPickContent?.(v.version)}>
                {isLoaded ? '고름 · 저장 전' : '이 버전으로 바꾸기'}
              </Button>
            ) : row.action === 'original' ? (
              /* **사진 줄 — 서버에 나가지 않는다.** 고르면 창의 [저장]이 `PATCH {spec:null}` 로
                 내보낸다(FigureModal.pickOriginal). 좌표·식 줄의 [이 버전 가져오기]와 같은 급이다. */
              <Button size="xs" variant={isLoaded ? 'primary' : 'secondary'} icon={<RotateCcw className="size-3" />}
                disabled={busy || !onPickOriginal || isLoaded}
                title={isLoaded
                  ? '이미 골랐습니다 — 창의 [저장]을 누르면 반영됩니다'
                  : '이 사진으로 바꾸기를 고릅니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                onClick={() => onPickOriginal?.(v.version)}>
                {isLoaded ? '고름 · 저장 전' : '이 사진으로 바꾸기'}
              </Button>
            ) : (
              /* **서버에 보내지 않는다.** 이 버전의 값을 편집 폼으로 옮겨 올 뿐이고, 저장은 [저장]이 한다.
                 종전에는 여기서 `restore` 를 바로 불렀고, 이력이 append-only 라(계약 §1-5)
                 버전을 둘러보는 것만으로 이력이 쌓였다(2026-08-30 오너 지적). 그 문제는 **좌표·식
                 버전**의 것이고, 그쪽에는 [보기]가 있어 둘러보기가 공짜다. */
              <Button size="xs" variant="secondary" icon={<RotateCcw className="size-3" />}
                disabled={busy || !canLoad}
                title={!canLoad
                  ? '지금은 가져올 수 없습니다 — 창 위 안내를 보세요'
                  : '이 버전의 값을 왼쪽 숫자 칸으로 가져옵니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                onClick={() => onLoad(v.version)}>
                이 버전 가져오기
              </Button>
            )}
            {/* 그 버전을 만든 사람의 지시(회신 §4-1). 첫 줄은 alt·시각·작성자·버튼이 이미 다 써서
                아래로 내린다. 사유가 300(재요청)인지로 가르지 않는다 — 서버가 어느 버전에 싣는지
                확정되지 않아, **값이 오면 보인다**로 둔다(없는 규칙을 지어내지 않는다). */}
            {v.user_prompt && (
              <p className="w-full truncate pl-10 text-[11px] text-gray-500" title={String(v.user_prompt)}>
                지시: {String(v.user_prompt)}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
